/**
 * Basil v0.2, P5 — a refusal names the limitation and an action that exists.
 *
 * The compiler's own `reason` strings are a record of WHAT it found ("you removed an answer",
 * "Basil can't find this answer on the page"). They are accurate and they are not instructions: a
 * writer who reads one still does not know what to do next. This module is the one place that turns
 * a held row into a sentence with a next step in it, and it is a VIEW MODEL: the grammar, the
 * compiler, the planner and the write path are untouched, and nothing here decides what may be sent.
 *
 * Two laws from the brief hold here:
 *
 *  * only the refusal that applies is shown, never a catalogue; a row carries one sentence;
 *  * "Open this scene" is the protected exit — the surface asks before discarding unapplied edits,
 *    and confirmed changes stay.
 */

/** `compile.ts:894` — an answer the writer took off the page, on a choice nothing is holding. */
export const ANSWER_REMOVED_REASON = 'you removed an answer';

/** `compile.ts:898` — an answer held because two lines could both be it. */
export const HELD_ANSWER_REASON = "Basil can't find this answer on the page";

/** `compile.ts:908` — the marker reason a line raises when more than one block could be it. */
export const AMBIGUOUS_IDENTITY_REASON = 'ambiguous identity';

/** `compile.ts:926` — "Basil found more than one answer this could be", with or without its tail. */
const AMBIGUOUS_ANSWER_PREFIX = 'Basil found more than one answer this could be';

/** Two answers on the page read the same, so no edit on either of them can be addressed. */
export const MATCHING_ANSWERS = 'Basil cannot tell which matching answer you changed. '
  + 'Open this scene to edit that answer.';

/** An answer taken off the page. Basil never deletes, so the scene's own editor does it. */
export const HELD_DELETION = 'Basil cannot remove this answer here. Open this scene to remove it.';

/** A scene the script grew. The canvas's own control is named, because that is the one that exists. */
export const NEW_SCENE = 'Basil cannot add scenes yet. '
  + 'Return to the story view and choose Create Node to add a scene.';

/**
 * The episode moved under the writer and NOTHING was sent on this attempt.
 *
 * "Reopening" rather than "reloading": the recorded reopen loses the text in the editor, and a
 * writer told to reload reasonably expects it to survive. The warning comes before the instruction
 * that would destroy the edits, not after it.
 */
export const CHANGED_ELSEWHERE = 'The episode changed elsewhere. Nothing was sent. '
  + 'Copy your edits somewhere safe before reopening Basil. '
  + 'Reopening discards edits you have not applied.';

/**
 * The recording failure before any send: what is actually known, and nothing more (T17-03).
 *
 * The pipeline observes one fact — the durable store would not open. A private window is ONE of the
 * things that causes it; cleared site data, a quota, a blocked storage setting and an extension are
 * others, and the surface cannot tell them apart. So the sentence names the browser, tells the
 * writer to protect their words, and points at the one action that fits every cause.
 *
 * It says "on this attempt" on purpose: a storage failure is a fact about THIS attempt, and an
 * earlier attempt's unconfirmed changes stay unconfirmed beside it (T17-02).
 */
export const NO_RECORD_IN_THIS_BROWSER = "Basil can't keep a record of changes in this browser, "
  + 'so nothing was sent on this attempt. '
  + 'Copy your edits somewhere safe, then ask for help with this browser.';

/**
 * The tail of the one refusal a writer's own attestation resolves (S-3).
 *
 * It names the control that carries the attestation, so the sentence and the button on the recovery
 * block cannot drift apart. It is a constant rather than a literal because TWO places need to know
 * it: the guard that writes the refusal, and the surface, which clears that refusal the moment the
 * attestation it names has been written (S-3c). A refusal that says "press I have checked the
 * episode" must not still be on the screen once the control has gone.
 */
export const SETTLE_FIRST = 'Download the change record, check the episode, '
  + 'then press I have checked the episode.';

/**
 * The same failure where a private window is INDEPENDENTLY DETECTED (the brief, B:49).
 *
 * Nothing in this client detects one. `auditStore.available()` reduces every database-open failure
 * to false, which is the cause of a private window and not evidence of it, and there is no browsing
 * mode probe anywhere in the tree. The sentence is therefore kept here, unused, against the day a
 * real detector exists: until then {@link NO_RECORD_IN_THIS_BROWSER} is what the pipeline files,
 * because advising a writer to leave a private window they are not in helps nobody.
 */
export const NO_RECORD_PRIVATE_WINDOW = "Basil can't keep a record of changes in this browser, "
  + 'so nothing was sent. Try a regular window instead of a private one.';

/** The held-row label for a real limitation. The stale check never wears it. */
export const CANNOT_DO_THIS_HERE = "Can't do this here";

/**
 * The protected exit, in the words every sentence that offers it uses.
 *
 * It lives here rather than in one of the panels because three surfaces now offer it — a held
 * change row, a suggestion card and a missing-name note in the margin — and all three must say the
 * same four words, because all three are the same action.
 */
export const OPEN_THIS_SCENE = 'Open this scene';

/** What a held row says, and whether its sentence offers the protected exit into the scene. */
export interface RowRefusal {
  /** the sentence the writer reads, in full. */
  text: string;
  /** true when the sentence says "Open this scene", so the row offers that exit. */
  opensScene: boolean;
}

const MATCHING = { text: MATCHING_ANSWERS, opensScene: true };

/**
 * The sentence for one held row, or null when the compiler's own reason is left as it stands.
 *
 * @param kind the structural item's kind (`new_scene`, `choice_shape`, …)
 * @param reason the compiler's own reason string for that item
 */
export function rowRefusal(kind: string | null | undefined, reason: string | null | undefined): RowRefusal | null {
  const said = String(reason ?? '');

  if (kind === 'new_scene') {
    return { text: NEW_SCENE, opensScene: false };
  }

  if (said === HELD_ANSWER_REASON || said === AMBIGUOUS_IDENTITY_REASON) {
    return MATCHING;
  }

  if (said.indexOf(AMBIGUOUS_ANSWER_PREFIX) === 0) {
    return MATCHING;
  }

  if (said === ANSWER_REMOVED_REASON) {
    return { text: HELD_DELETION, opensScene: true };
  }

  return null;
}
